import { useFocusEffect } from "expo-router";
import { setStatusBarStyle } from "expo-status-bar";
import { useColorScheme } from "nativewind";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Platform, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { ScreenTitleBar } from "@/components/navigation/ScreenTitleBar";
import { Text } from "@/components/ui/text";
import { palette } from "@/constants/colors";
import { ActiveMedicalRequestsSection } from "@/features/medicalRequest";
import { useChildren } from "@/features/selectedChild";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import {
    useGetLeaveRequestsForChildrenQuery,
    useGetMedicalRequestsForChildrenQuery,
    useGetParentMessagesQuery,
    useGetPickupRequestsForChildrenQuery,
} from "@/query/apiSlice";

import { ChildAbsenceSummarySection } from "./ChildAbsenceSummarySection";
import { HomeRequestActions } from "./HomeRequestActions";
import { canParentResubmitLeaveRequest } from "./leaveRequestStatus";

const BOTTOM_OVERSCROLL_FILL_HEIGHT = 500;

export function HomeScreen() {
    const { t } = useTranslation();
    const { colorScheme } = useColorScheme();
    const { children, refetch: refetchChildren } = useChildren();
    const childIds = useMemo(
        () => children.map((child) => child.id),
        [children],
    );
    const medicalRequestsEnabled = CONFIG.featureMedicalRequestsEnabled;

    const { data: leaveRequests = [], refetch: refetchLeaveRequests } =
        useGetLeaveRequestsForChildrenQuery(childIds, {
            skip: childIds.length === 0,
        });

    const { refetch: refetchMedicalRequests } =
        useGetMedicalRequestsForChildrenQuery(childIds, {
            skip: childIds.length === 0 || !medicalRequestsEnabled,
        });

    const { data: pickupRequests = [], refetch: refetchPickupRequests } =
        useGetPickupRequestsForChildrenQuery(childIds, {
            skip: childIds.length === 0,
        });
    const { refetch: refetchParentMessages } = useGetParentMessagesQuery();

    const childIdsNeedingInfo = useMemo(() => {
        const ids = new Set<number>();
        for (const request of leaveRequests) {
            if (canParentResubmitLeaveRequest(request.status)) {
                ids.add(request.child_id);
            }
        }
        return ids;
    }, [leaveRequests]);

    const onRefreshData = useCallback(
        () =>
            Promise.all([
                refetchChildren(),
                refetchLeaveRequests(),
                medicalRequestsEnabled && refetchMedicalRequests(),
                refetchPickupRequests(),
                refetchParentMessages(),
            ]),
        [
            refetchChildren,
            refetchLeaveRequests,
            refetchMedicalRequests,
            refetchPickupRequests,
            refetchParentMessages,
            medicalRequestsEnabled,
        ],
    );
    const { refreshControl } = usePullToRefresh(onRefreshData, {
        tintColor: palette.light.surface,
    });

    useFocusEffect(
        useCallback(() => {
            setStatusBarStyle("light");
            return () => {
                setStatusBarStyle(colorScheme === "dark" ? "light" : "dark");
            };
        }, [colorScheme]),
    );

    return (
        <View className="flex-1 bg-primary">
            <SafeAreaView edges={["top"]} className="flex-1">
                <ScrollView
                    className="flex-1 bg-primary"
                    contentContainerClassName="grow"
                    refreshControl={refreshControl}
                >
                    <View className="relative z-10 bg-primary">
                        <View className="mb-3">
                            <ScreenTitleBar
                                title={t("home.title")}
                                tone="onPrimary"
                            />
                            <Text
                                variant="caption"
                                className="mt-6 px-5 uppercase tracking-wide text-primary-foreground/80"
                            >
                                {t("home.requestsTitle")}
                            </Text>
                        </View>
                        <View className="-mb-12 px-4">
                            <HomeRequestActions
                                disabled={children.length === 0}
                            />
                        </View>
                    </View>
                    <View className="grow gap-6 bg-background px-4 pb-6 pt-16">
                        <ChildAbsenceSummarySection
                            childList={children}
                            childIdsNeedingInfo={childIdsNeedingInfo}
                            pickupRequests={pickupRequests}
                        />
                        {medicalRequestsEnabled && (
                            <ActiveMedicalRequestsSection
                                childList={children}
                            />
                        )}
                    </View>
                    {Platform.OS !== "web" ? (
                        <View
                            pointerEvents="none"
                            className="bg-background"
                            style={{
                                height: BOTTOM_OVERSCROLL_FILL_HEIGHT,
                                marginBottom: -BOTTOM_OVERSCROLL_FILL_HEIGHT,
                            }}
                        />
                    ) : null}
                </ScrollView>
            </SafeAreaView>
        </View>
    );
}
